import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, Shield, FileText, CheckCircle2, TrendingUp, Zap, Server } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Python 3.11', icon: Server },
  { name: 'spaCy NER', icon: Database },
  { name: 'ONNX Runtime INT8', icon: Cloud },
  { name: 'HuggingFace Transformers', icon: Shield },
  { name: 'scikit-learn / TF-IDF', icon: FileText },
];

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '85%+',
    label: 'Extraction Accuracy',
    description: 'Improved from a ~40% baseline through hierarchical pattern matching',
  },
  {
    icon: Zap,
    value: '350K+',
    label: 'Records/Minute',
    description: 'Sustained processing throughput at production scale',
  },
  {
    icon: Database,
    value: '21M',
    label: 'Records Processed',
    description: 'In a single batch in under 60 minutes',
  },
  {
    icon: CheckCircle2,
    value: '<$50',
    label: 'Per Million Records',
    description: 'Fully offline, zero external API dependency',
  },
];

const highlights = [
  {
    category: 'Classification Excellence',
    items: [
      'Improved product extraction accuracy from ~40% to 85%+ through hierarchical pattern matching and transformer-based classification',
      'Achieved ~80% attribute extraction coverage across Brand, Type, Processing, Grade, Form, and Origin fields',
      'Maintained classification performance consistently across multilingual inputs without any external API dependencies',
    ],
  },
  {
    category: 'Scale and Cost Efficiency',
    items: [
      'Processed 21 million trade records in under 60 minutes at 350K+ records per minute sustained throughput',
      'Deployed fully offline at under $50 per million records eliminating cloud inference costs entirely',
      'ONNX INT8 quantization delivered production inference speed on CPU without GPU infrastructure',
    ],
  },
];

const challenges = [
  {
    title: 'Product Over-Segmentation Breaking Extraction Accuracy',
    icon: Database,
    problem:
      "Single product descriptions like 'INDIAN GREEN COFFEE' were being incorrectly split into multiple separate entities, causing systematic extraction failures that drove baseline accuracy to ~40%.",
    solution:
      'Implemented hierarchical pattern matching that identifies and preserves compound product names as single entities, prioritizing longer, more specific patterns before attempting shorter generic matches.',
    impact: 'Product extraction accuracy improved from ~40% to 85%+',
  },
  {
    title: 'Multilingual and Noisy Input Data',
    icon: FileText,
    problem:
      'Shipment descriptions contained mixed languages, shipping terminology, container details, unit inconsistencies, and arbitrary formatting — breaking any language-specific model trained on clean data.',
    solution:
      'Built a comprehensive preprocessing pipeline with automatic language detection, noise removal for shipping terms and container markers, unit normalization, and multilingual support feeding into cross-lingual extraction models.',
    impact: 'Consistent extraction performance across multilingual datasets with no manual preprocessing required',
  },
  {
    title: 'Enterprise-Scale Volume at Prohibitive Cost',
    icon: Cloud,
    problem:
      'Processing 21 million records through cloud API inference would be cost-prohibitive and violate secure offline deployment requirements for the enterprise environment.',
    solution:
      'Applied ONNX INT8 quantization to compress models for CPU inference and joblib multiprocessing for parallelized batch processing, achieving production throughput without cloud dependency.',
    impact: '21 million records processed in under 60 minutes at under $50 per million records, fully offline',
  },
  {
    title: 'Training Data Misalignment Causing Systematic Errors',
    icon: Shield,
    problem:
      'Model outputs did not match the format and patterns in training data, creating systematic misclassification that could not be resolved by retraining without fixing the underlying alignment issue.',
    solution:
      'Audited and realigned all extraction patterns against actual training data examples, iteratively validating outputs against expected formats before finalising the production pipeline.',
    impact: 'Systematic misclassification eliminated with consistent 85%+ accuracy across production volumes',
  },
];

const HSNClassification = () => {
  return (
    <>
      <SEOHead
        title="AI-Powered HSN Classification Case Study | Agility AI"
        description="How Agility processed 21 million international trade records in under 60 minutes using hybrid ML — improving extraction accuracy from 40% to 85%+ at under $50 per million records, fully offline."
        url="https://www.agilitytech.ai/case-studies/hsn-classification"
        type="article"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'AI-Powered HSN Classification & Product Categorization',
          description:
            'Processing 21 million international trade shipment records in under 60 minutes using a hybrid ML pipeline for HSN code classification and multilingual product attribute extraction at under $50 per million records.',
          author: { '@type': 'Organization', name: 'Agility AI' },
          publisher: { '@type': 'Organization', name: 'Agility AI' },
        }}
      />
      <div className="pt-20 bg-black">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&q=80"
              alt="HSN Classification Banner"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
                <span className="text-teal-400 text-sm font-medium">Global Trade</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                AI-Powered HSN Classification & Product Categorization
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
                Processing 21 million international trade shipment records in under 60 minutes using a hybrid ML pipeline for HSN code classification and multilingual product attribute extraction at under $50 per million records.
              </p>
            </div>
          </div>
        </section>

        {/* Key Metrics */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {keyMetrics.map((metric, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <metric.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Project Details */}
        <section className="py-12 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
                <p className="text-white">Global Trade</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
                <p className="text-white">Global</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
                <p className="text-white">Enterprise — 21M+ Record Production Dataset</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
                <p className="text-white">Q4 2024 - Completed</p>
              </div>
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="py-12 bg-gray-900 border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {technologies.map((tech, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30"
                >
                  <tech.icon className="h-6 w-6 text-teal-400" />
                  <span className="text-gray-300 font-medium">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Challenge Overview */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <FileText className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">
                      Unstructured Multilingual Trade Data at Enterprise Scale
                    </h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      International trade shipment descriptions arrive in multiple languages, inconsistent formats, and with missing qualifiers — making automated HSN code classification and structured product extraction extremely difficult at scale. The existing system achieved only ~40% product extraction accuracy due to over-segmentation of product names, missing key qualifiers, and training data misalignment with real-world shipment descriptions. Cloud API-based solutions were prohibitively expensive at the volumes required and could not meet offline deployment requirements.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="py-20 bg-black border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">Transformational Results</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
                <div className="flex items-start space-x-4 mb-8">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">
                      Enterprise-Scale Offline Classification Engine
                    </h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      We replaced a failing ~40% accuracy system with a production-grade hybrid ML pipeline processing 21 million trade records in under 60 minutes at 350K+ records per minute. The hierarchical product extraction engine eliminates over-segmentation, the NER-based attribute extractor handles multilingual inputs, and ONNX INT8 quantization delivers all of this fully offline at under $50 per million records — with zero external API dependency and no GPU infrastructure required.
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {highlights.map((category, index) => (
                  <div
                    key={index}
                    className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30"
                  >
                    <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                    <div className="space-y-4">
                      {category.items.map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                          <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Challenges & Solutions */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {challenges.map((challenge, index) => (
                <div
                  key={index}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300"
                >
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                      <challenge.icon className="h-6 w-6 text-teal-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>Problem
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>Solution
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                    </div>
                    <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                      <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                      <p className="text-teal-300 font-medium">{challenge.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Ready to Classify Trade Data at Enterprise Scale?
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Contact our ML engineering team to discover how hybrid AI pipelines can transform your international trade data operations.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Get Started Today</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HSNClassification;
